/**
 * `<caos-inspector>` — properties of the current selection, edited in place.
 * (Catalog: Editors & builders › Inspector — properties of the current selection, edited in place.)
 *
 * A side/detail panel: a header carrying the `heading`, an optional `actions` slot, and
 * optional close control (`closable`), above the default slot (the body — typically
 * `caos-detail-section` / `caos-key-value-row` composed via slots). By default the panel is a
 * labelled `role="complementary"` landmark, labelled by the header title via `aria-labelledby`.
 *
 * When `modal` is set it COMPOSES `<caos-overlay>` rather than being a dialog itself (CAOS-429):
 * the scrim, the `role="dialog"`/`aria-modal` panel, the focus trap, the focus return to the
 * opener and Escape all come from that one element, and this one keeps only its own job — the
 * properties of the current selection, edited in place. The overlay's dialog is labelled with the
 * `heading`, because an `aria-labelledby` IDREF cannot cross the shadow boundary between this
 * component's title and the overlay's panel.
 *
 * Closing fires a cancelable, composed `caos-inspector-close` however it was asked for — the close
 * control, Escape, or the scrim — so a host listens for one event rather than two. Styling seams:
 * `::part(panel)`, `::part(header)`, `::part(title)`, `::part(actions)`, `::part(close)`,
 * `::part(body)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { makeIcon } from './icons.js';
import './overlay.js';

let uid = 0;

const sheet = makeSheet(`
  :host { display: block; }

  .panel {
    display: flex;
    flex-direction: column;
    max-height: 100%;
    background: var(--caos-color-surface);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-lg);
    overflow: hidden;
  }
  /* Modal: the overlay owns the box. Its panel draws the surface, border, radius and the one
     float elevation reserved for overlays, and is what the scrim sits behind — drawing them here
     too would frame the panel twice. The host stops taking layout space as well, because the
     overlay it now contains is fixed to the viewport. */
  :host([modal]) { display: contents; }
  :host([modal]) .panel {
    border: 0;
    border-radius: 0;
    box-shadow: none;
    /* Sized by the overlay rather than by a hard height: as a flex item in its panel this fills a
       drawer, whose height is definite, and hugs its content in a centred modal, whose height is
       not. A height of 100% could not tell those two apart and would stretch a short modal to
       fill the line. min-height lets it shrink past its content so the body scrolls rather than
       overflowing. */
    flex: 1 1 auto;
    min-height: 0;
  }

  .header {
    display: flex;
    align-items: center;
    gap: var(--caos-space-3);
    padding: var(--caos-space-3) var(--caos-space-4);
    border-bottom: 1px solid var(--caos-color-border);
    background: var(--caos-color-surface);
  }
  .title {
    flex: 1 1 auto;
    min-width: 0;
    font-family: var(--caos-font-display);
    font-weight: 600;
    font-size: 15px;
    line-height: 1.3;
    color: var(--caos-color-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .actions { flex: none; display: flex; align-items: center; gap: var(--caos-space-2); }
  .actions:not(:has(*)) { display: none; }

  .close {
    flex: none;
    display: none;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 4px;
    margin: -2px -4px -2px 0;
    appearance: none;
    background: transparent;
    border: none;
    border-radius: var(--caos-radius-sm);
    color: var(--caos-color-text-muted);
    cursor: pointer;
  }
  :host([closable]) .close, :host([modal]) .close { display: inline-flex; }
  .close:hover { background: var(--caos-color-surface-2); color: var(--caos-color-text); }
  .close:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  .close svg { display: block; width: 18px; height: 18px; }

  .body { padding: var(--caos-space-4); overflow: auto; }
`);

export class CaosInspector extends CaosElement {
  static get observedAttributes(): string[] {
    return ['heading', 'modal'];
  }

  private panel: HTMLDivElement;
  private titleEl: HTMLDivElement;
  private closeBtn: HTMLButtonElement;
  private overlay: HTMLElement | null = null;
  private readonly seq = ++uid;

  constructor() {
    super(sheet);

    this.panel = document.createElement('div');
    this.panel.className = 'panel';
    this.panel.setAttribute('part', 'panel');

    const header = document.createElement('div');
    header.className = 'header';
    header.setAttribute('part', 'header');

    this.titleEl = document.createElement('div');
    this.titleEl.className = 'title';
    this.titleEl.setAttribute('part', 'title');
    this.titleEl.id = `caos-inspector-title-${this.seq}`;

    const actions = document.createElement('div');
    actions.className = 'actions';
    actions.setAttribute('part', 'actions');
    const actionsSlot = document.createElement('slot');
    actionsSlot.name = 'actions';
    actions.append(actionsSlot);

    this.closeBtn = document.createElement('button');
    this.closeBtn.type = 'button';
    this.closeBtn.className = 'close';
    this.closeBtn.setAttribute('part', 'close');
    this.closeBtn.setAttribute('aria-label', 'Close');
    this.closeBtn.append(makeIcon('close'));
    this.closeBtn.addEventListener('click', () => this.requestClose());

    header.append(this.titleEl, actions, this.closeBtn);

    const body = document.createElement('div');
    body.className = 'body';
    body.setAttribute('part', 'body');
    body.append(document.createElement('slot'));

    this.panel.append(header, body);
    this.root.append(this.panel);
  }

  connectedCallback(): void {
    this.sync();
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  /** Fire the cancelable close request (the host decides whether to remove/hide). */
  requestClose(): void {
    this.dispatchEvent(
      new CustomEvent('caos-inspector-close', { bubbles: true, composed: true, cancelable: true }),
    );
  }

  private sync(): void {
    const heading = this.getAttribute('heading') ?? '';
    this.titleEl.textContent = heading;

    if (this.hasAttribute('modal')) {
      // The dialog role, aria-modal and the label belong to the overlay's panel — that is the
      // element the scrim sits behind and the trap runs inside. This panel is then ordinary
      // content within the dialog, and must not also announce itself as a landmark.
      this.panel.removeAttribute('role');
      this.panel.removeAttribute('aria-modal');
      this.panel.removeAttribute('aria-labelledby');
      this.mountOverlay().setAttribute('label', heading);
    } else {
      this.unmountOverlay();
      this.panel.setAttribute('role', 'complementary');
      this.panel.removeAttribute('aria-modal');
      this.panel.setAttribute('aria-labelledby', this.titleEl.id);
    }
  }

  /** Move the panel inside a `<caos-overlay>`, creating it on the first modal render. */
  private mountOverlay(): HTMLElement {
    if (this.overlay) return this.overlay;
    const overlay = document.createElement('caos-overlay');
    // One act, one public event. The overlay's close request is composed, so it would otherwise
    // surface from this component as a SECOND name for being dismissed; it is translated here
    // into the event this component has always fired and its hosts already listen for.
    overlay.addEventListener('caos-overlay-close', (e: Event) => {
      e.stopPropagation();
      this.requestClose();
    });
    overlay.append(this.panel);
    this.root.append(overlay);
    this.overlay = overlay;
    return overlay;
  }

  private unmountOverlay(): void {
    if (!this.overlay) return;
    this.root.append(this.panel);
    this.overlay.remove();
    this.overlay = null;
  }

}

define('caos-inspector', CaosInspector);

declare global {
  interface HTMLElementTagNameMap {
    'caos-inspector': CaosInspector;
  }
}
